import React, { useEffect, useState } from "react";
import { Badge, Card, Col, Row } from "react-bootstrap";
import { Link } from "react-router-dom";

import { rockGemsApi } from "../../services/rockGemsApi";
import LoadingBlock from "../common/LoadingBlock";

import RockGemLikeDetail, { isGemLikeRockData } from "../rock/RockGemLikeDetail";
import {
  normalizeIdentifyTaskResult,
  taskResultDisplayName,
} from "../../utils/normalizeIdentifyTaskResult";

function InfoRow({ label, value }) {
  return (
    <tr>
      <td className="text-muted small" style={{ width: 180, whiteSpace: "nowrap" }}>
        {label}
      </td>
      <td style={{ wordBreak: "break-all" }}>{value ?? "—"}</td>
    </tr>
  );
}

function PhotoGallery({ photos }) {
  const items = (photos || []).filter(Boolean);
  if (!items.length) return <div className="text-muted small">No photos.</div>;
  return (
    <Row className="g-2">
      {items.map((src, i) => (
        <Col key={i} xs={6} sm={4} md={3}>
          <a href={src} target="_blank" rel="noopener noreferrer">
            <img
              alt=""
              src={src}
              style={{
                width: "100%",
                aspectRatio: "1 / 1",
                objectFit: "cover",
                borderRadius: 8,
              }}
            />
          </a>
        </Col>
      ))}
    </Row>
  );
}

function EstimatePriceResultCard({ rd, languageCode }) {
  const uid = rd?.uid;
  const [gem, setGem] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!uid) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    rockGemsApi
      .getGemDetail({ uid, language_code: languageCode || "en" })
      .then((data) => { if (!cancelled) setGem(data); })
      .catch((e) => { if (!cancelled) setError(e); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [uid, languageCode]);

  if (loading) return <LoadingBlock label="Loading gem info..." />;

  if (error || !gem) {
    return (
      <Card>
        <Card.Body className="text-muted small">
          {uid ? `No gem found for uid: ${uid}` : "No UID in result."}
        </Card.Body>
      </Card>
    );
  }

  return <RockGemLikeDetail gem={gem} showCrawlFields={false} />;
}

function UnifiedResultCard({ rd }) {
  return (
    <Card className="border-warning">
      <Card.Header className="bg-warning-subtle">
        <Card.Title className="mb-0 h6">
          Legacy unified result
          <span className="text-muted fw-normal ms-2">(sparse / pre–gem enrichment)</span>
        </Card.Title>
      </Card.Header>
      <Card.Body>
        <Row className="g-3">
          <Col xs={12} lg={6}>
            <table className="table table-sm table-borderless mb-0">
              <tbody>
                <InfoRow label="Name" value={rd.name ?? rd.latin_name} />
                <InfoRow label="Common name" value={rd.common_name} />
                <InfoRow label="Rock type" value={rd.rock_type} />
                <InfoRow label="Rarity" value={rd.rarity} />
                <InfoRow label="Group" value={rd.group} />
                <InfoRow label="Is rock" value={rd.is_rock != null ? String(rd.is_rock) : null} />
                <InfoRow label="Identification ID" value={rd.identification_id} />
              </tbody>
            </table>
          </Col>
          <Col xs={12} lg={6}>
            <div className="mb-2 fw-semibold small text-muted text-uppercase">Photos</div>
            <PhotoGallery photos={rd.photo_list} />
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );
}

/**
 * Renders recognition output from task.stored_result / result_data.
 */
export default function TaskRecognitionResult({ task, showGemCatalogLink = true }) {
  const normalized = normalizeIdentifyTaskResult(task);

  if (normalized.kind === "empty") {
    return (
      <Card>
        <Card.Header>
          <strong>Recognition result</strong>
        </Card.Header>
        <Card.Body className="text-muted">No stored result.</Card.Body>
      </Card>
    );
  }

  const title = taskResultDisplayName(normalized);
  const rd = normalized.data;
  const gemLike = normalized.kind === "gem" || isGemLikeRockData(rd);

  if (normalized.kind === "price") {
    return (
      <div>
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
          <h5 className="mb-0">
            Recognition result
            <Badge bg="info" text="dark" className="ms-2">Estimate Price</Badge>
          </h5>
          {showGemCatalogLink && rd?.uid ? (
            <Link
              to={`/gems/${encodeURIComponent(rd.uid)}?language_code=${encodeURIComponent(task?.language_code || "en")}`}
              className="btn btn-outline-primary btn-sm"
            >
              Open in gems catalog
            </Link>
          ) : null}
        </div>
        <EstimatePriceResultCard rd={rd} languageCode={task?.language_code} />
      </div>
    );
  }

  if (gemLike) {
    return (
      <div>
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
          <h5 className="mb-0">
            Recognition result
            <span className="text-muted fw-normal ms-2">{title}</span>
          </h5>
          {showGemCatalogLink && rd?.uid ? (
            <Link
              to={`/gems/${encodeURIComponent(rd.uid)}?language_code=${encodeURIComponent(task?.language_code || "en")}`}
              className="btn btn-outline-primary btn-sm"
            >
              Open in gems catalog
            </Link>
          ) : null}
        </div>
        <RockGemLikeDetail gem={rd} showCrawlFields={Boolean(rd?.cms_crawl_status)} />
      </div>
    );
  }

  return (
    <div>
      <h5 className="mb-3">
        Recognition result
        <Badge bg="warning" text="dark" className="ms-2">
          Unified
        </Badge>
        <span className="text-muted fw-normal ms-2">{title}</span>
      </h5>
      <UnifiedResultCard rd={rd} />
    </div>
  );
}
